import { Backdrop } from 'components/Backdrop';
import { Snackbar } from 'components/Snackbar';
import { IUseDisclose, useDisclose, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { TreatmentOnGoingService } from 'services';
import { ITreatmentOnGoing } from 'types';

import { AlertColor } from '@mui/material';

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export interface ITreatmentOnGoingContext {
  treatmentOnGoing: ITreatmentOnGoing;
  loading: boolean;
  finishTreatmentOnGoing: () => Promise<void>;
  riskClassificationModal: IUseDisclose;
  petInformationsModal: IUseDisclose;
  tutorLocationModal: IUseDisclose;
  imageModal: IUseDisclose;
  documentModal: IUseDisclose;
  petFilesModal: IUseDisclose; 
  imgs: any[];
  _docs: any[];
}

export const TreatmentOnGoingContext = createContext<ITreatmentOnGoingContext>(
  {} as ITreatmentOnGoingContext
);

type Props = {
  children: ReactNode;
};

export const TreatmentOnGoingProvider = ({ children }: Props) => {
  const [treatmentOnGoing, setTreatmentOnGoing] = useState<ITreatmentOnGoing>(
    {} as ITreatmentOnGoing
  );
  const [imgs, setImgs] = useState<any[]>([]);
  const [_docs, _setDocs] = useState<any[]>([]);
  const { showLoader, hideLoader, loading } = useLoader(false);

  const riskClassificationModal = useDisclose();
  const petInformationsModal = useDisclose();
  const tutorLocationModal = useDisclose();
  const imageModal = useDisclose();
  const documentModal = useDisclose();
  const petFilesModal = useDisclose();
  
  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });
  const { replace } = useHistory();
  
  

  const getTreatmentOnGoing = useCallback(async () => {
    try {
      const data = await TreatmentOnGoingService.getCurrentTreatmentOnGoing();
      setTreatmentOnGoing(data);
      fetchImages(data)
      fetchDocuments(data)
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    }
  }, []);

  const fetchImages = useCallback((treatmentOnGoing)=>{
    let imgArray: any[] = []  
    const images = treatmentOnGoing.documents.flatMap((document: { name: string; }) => { if (document.name.split('.')[1] !== 'pdf') {return document} }).filter((image: any) => image);
      images.map(async (image: any)=>{ 
        const img = await TreatmentOnGoingService.downloadImage(treatmentOnGoing.id, image._id) 
        imgArray.push(img)
        
      }
    )  
      
     
    setImgs(imgArray)
  },[])

  const fetchDocuments = useCallback((treatmentOnGoing) =>{
      let docsArray: any[] = [];
      const docs = treatmentOnGoing.documents.flatMap((document: { name: string; }) => { if (document.name.split('.')[1] === 'pdf') {return document} }).filter((document: any) => document);
      docs.map(async (document: any)=>{ 
        const doc = await TreatmentOnGoingService.downloadImage(treatmentOnGoing._id, document?._id) 
          docsArray.push({base64: doc, name: document.name});
        }
      )  
        _setDocs(docsArray)
  },[])

  const finishTreatmentOnGoing = async () => {
    if (!treatmentOnGoing) {
      return;
    }

    try {
      showLoader();

      await TreatmentOnGoingService.finishTreatmentOnGoing({
        roomId: treatmentOnGoing.roomId,
        treatmentId: treatmentOnGoing._id,
      });

      await new Promise((r) => {
        setTimeout(r, 3000);
      });

      setSnackbar({
        message: 'Atendimento finalizado com sucesso',
        variant: 'success',
      });

      replace('/vet/dashboard/');
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    getTreatmentOnGoing()
  }, []);

  return (
    <TreatmentOnGoingContext.Provider
      value={{
        treatmentOnGoing,
        loading,
        finishTreatmentOnGoing,
        riskClassificationModal,
        petInformationsModal,
        tutorLocationModal,
        documentModal,
        imageModal,
        petFilesModal,
        imgs,
        _docs
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />

      <Backdrop open={loading} />
    </TreatmentOnGoingContext.Provider>
  );
};

export const useTreatmentOnGoing = () => {
  return useContext(TreatmentOnGoingContext);
};
